'use client';

import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { trackEmerald } from '../analytics';
import { useFlowState } from '../FlowProvider';
import { useSphereHandle } from '../sphereContext';
import {
  DragHint,
  InsightBlock,
  InsightBody,
  InsightTitle,
  Note,
  OutlineCta,
  PrimaryCta,
  Scene,
  SceneBottom,
  SceneSpacer,
  SceneTop,
  ShareCaption,
  TextScrim,
} from '../styles';
import {
  buildDeck,
  localNowMinutes,
  moodFor,
  sentenceCase,
} from '../weather/insightDeck';
import { DragCarousel } from './DragCarousel';

export type InsightsSceneProps = {
  active: boolean;
  orbWrapRef: RefObject<HTMLDivElement | null>;
  reducedMotion: boolean;
};

function downloadBlob(blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ultrasphere-story.png';
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** Act 4 — the rotating insight block and the share CTAs. */
export const InsightsScene = ({
  active,
  orbWrapRef,
  reducedMotion,
}: InsightsSceneProps) => {
  const { phase, userName, wx, wxAt } = useFlowState();
  const sphereRef = useSphereHandle();

  const deck = useMemo(() => {
    if (phase !== 'insights') return [];
    const nowM = localNowMinutes(wx, wxAt);
    return buildDeck(wx, userName, nowM);
  }, [phase]);

  const [cardIndex, setCardIndex] = useState(0);
  const [cardVisible, setCardVisible] = useState(true);
  const [dragHintOn, setDragHintOn] = useState(false);
  const [userRotated, setUserRotated] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareNote, setShareNote] = useState('');
  const fadeTimeoutRef = useRef<number | null>(null);
  // `isSharing` state is stale within the same tick, so a double-click passes it
  // twice and shares twice. This ref flips before any await.
  const sharingRef = useRef(false);

  useEffect(() => {
    if (!active) {
      setDragHintOn(false);
      return;
    }
    setCardIndex(0);
    setCardVisible(true);
    setUserRotated(false);
    const id = window.setTimeout(() => setDragHintOn(true), 1600);
    return () => window.clearTimeout(id);
  }, [active]);

  useEffect(
    () => () => {
      if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
    },
    [],
  );

  // Palette hand-off, once, on entering insights — where sky resolution lands
  // from the headless useWeatherFlow.
  useEffect(() => {
    if (!active) return;
    sphereRef.current?.setWeatherPalette(moodFor(wx), { wind: wx?.wind });
    sphereRef.current?.pulse(1.2);
  }, [active]);

  // Stable identity — DragCarousel's pointer effect must not re-attach mid-drag.
  const handlePageChange = useCallback((i: number) => {
    setCardVisible(false);
    setUserRotated(true);
    if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = window.setTimeout(() => {
      setCardIndex(i);
      setCardVisible(true);
    }, 160);
  }, []);

  const handleUserRotate = useCallback(() => setUserRotated(true), []);

  const currentCard = deck[cardIndex] ?? deck[0];

  // Every card owns its title slot, including the hello card whose title IS the
  // greeting — there is deliberately no separate persistent greeting line.
  const titleText = currentCard?.title ?? '';
  const bodyText = currentCard?.body ?? '';

  const shareCard = async (share: boolean) => {
    if (!currentCard || sharingRef.current) return;
    sharingRef.current = true;
    setIsSharing(true);
    setShareNote('');
    try {
      if (share) {
        // Mood only, never the name/greeting on the card — see analytics.ts.
        trackEmerald('emerald_share_clicked', { mood: moodFor(wx) });
        // A bare URL, never the raw PNG, carrying exactly one param so shared
        // links stay visible in Vercel Web Analytics. See README ("Share + OG image").
        const shareUrl = new URL(window.location.href);
        shareUrl.search = '';
        if (userName) shareUrl.searchParams.set('name', userName);
        else shareUrl.searchParams.set('shared', '1');

        const shareMessage = 'Get a first look at Emerald.';
        const shareData = {
          title: 'Ultrahuman UltraSphere',
          text: shareMessage,
          url: shareUrl.toString(),
        };

        // Returns false when the Clipboard API is absent OR the write is denied,
        // so the note never claims "copied" when nothing was.
        const copyLink = async (): Promise<boolean> => {
          try {
            if (navigator.clipboard?.writeText) {
              await navigator.clipboard.writeText(
                `${shareMessage}\n${shareData.url}`,
              );
              return true;
            }
          } catch {
            /* clipboard unavailable or denied — fall through to false */
          }
          return false;
        };
        const noteFor = (copied: boolean) =>
          copied
            ? 'LINK COPIED — PASTE TO SHARE.'
            : 'COULDN’T SHARE — TRY AGAIN, OR SAVE THE IMAGE BELOW.';

        if (typeof navigator !== 'undefined' && navigator.share) {
          try {
            await navigator.share(shareData);
            setShareNote('SHARED.');
          } catch (err) {
            // AbortError = user dismissed the share sheet: not a failure.
            if (err instanceof Error && err.name !== 'AbortError') {
              setShareNote(noteFor(await copyLink()));
            }
          }
        } else {
          setShareNote(noteFor(await copyLink()));
        }
        return;
      }

      trackEmerald('emerald_image_saved', { mood: moodFor(wx) });
      const blob = await sphereRef.current?.exportPNG({
        tag: sentenceCase(currentCard.tag),
        title: currentCard.title,
        body: currentCard.body,
      });
      if (!blob) throw new Error('no-blob');
      downloadBlob(blob);
      setShareNote('SAVED. STORY-SIZED AT 1080×1920.');
    } catch {
      setShareNote('Could not prepare the image. Try again, or save later.');
    } finally {
      sharingRef.current = false;
      setIsSharing(false);
    }
  };

  return (
    <>
      <Scene $on={active} aria-hidden={!active}>
        <SceneTop>
          <TextScrim>
            <InsightBlock $visible={cardVisible} aria-live="polite">
              <InsightTitle>{titleText}</InsightTitle>
              <InsightBody>{bodyText}</InsightBody>
            </InsightBlock>
          </TextScrim>
        </SceneTop>
        <SceneSpacer />
        <SceneBottom>
          <ShareCaption>
            Everyone&rsquo;s next best action is different.
          </ShareCaption>
          <PrimaryCta
            type="button"
            disabled={isSharing}
            onClick={() => shareCard(true)}
          >
            {isSharing ? 'preparing…' : 'Share with your circle'}
          </PrimaryCta>
          <OutlineCta
            type="button"
            disabled={isSharing}
            onClick={() => shareCard(false)}
          >
            {isSharing ? 'preparing…' : 'Save it. Share it. Tag us.'}
          </OutlineCta>
          <Note role="status" aria-live="polite">
            {shareNote}
          </Note>
        </SceneBottom>
      </Scene>
      {/* Positioned relative to Stage, not Scene, so it hugs the perched orb. */}
      <DragHint $on={dragHintOn && !userRotated} aria-hidden="true">
        DRAG THE ULTRASPHERE
      </DragHint>
      {active && deck.length > 0 && (
        <DragCarousel
          deck={deck}
          orbWrapRef={orbWrapRef}
          active={active}
          reducedMotion={reducedMotion}
          onPageChange={handlePageChange}
          onUserRotate={handleUserRotate}
        />
      )}
    </>
  );
};
